import type { HTMLAttributes, LiHTMLAttributes, ReactNode } from 'react'

import React from 'react'

import { cn } from '@/utils/shadcn/utils'

interface BondSearchInputDropdownProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  placement?: 'up' | 'down'
}

export const BondSearchInputDropdown = React.forwardRef<
  HTMLDivElement,
  BondSearchInputDropdownProps
>(function BondSearchInputDropdown(
  { className, children, placement = 'up', ...props },
  ref,
) {
  return (
    <div
      className={cn(
        'absolute left-0 right-0 z-50 bg-background border-none rounded-md shadow-lg w-[400px]',
        placement === 'up' ? 'bottom-full mb-1' : 'top-full mt-1',
        className,
      )}
      role="presentation"
      aria-hidden="true"
      ref={ref}
      {...props}
    >
      {children}
    </div>
  )
})

interface BondSearchInputResultsListProps
  extends HTMLAttributes<HTMLUListElement> {
  children: ReactNode
}

export const BondSearchInputResultsList = React.forwardRef<
  HTMLUListElement,
  BondSearchInputResultsListProps
>(function BondSearchInputResultsList({ className, children, ...props }, ref) {
  return (
    <ul
      className={cn(
        'divide-y bg-background divide-secondary-background max-h-60 overflow-auto w-full rounded-lg',
        className,
      )}
      tabIndex={-1}
      // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: Bulk suppression as part of biome migration
      role="listbox"
      aria-label="Bond search results"
      ref={ref}
      {...props}
    >
      {children}
    </ul>
  )
})

interface BondSearchInputResultItemProps
  extends LiHTMLAttributes<HTMLLIElement> {
  children: ReactNode
  isFocused?: boolean
}

export function BondSearchInputResultItem({
  className,
  children,
  isFocused,
  ...props
}: BondSearchInputResultItemProps) {
  return (
    <li
      className={cn(
        'bg-background flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-secondary-background w-full ',
        isFocused && 'bg-secondary-background',
        className,
      )}
      // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: Bulk suppression as part of biome migration
      role="option"
      tabIndex={0}
      aria-selected={isFocused}
      {...props}
    >
      {children}
    </li>
  )
}

interface BondSearchInputContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export function BondSearchInputContainer({
  className,
  children,
  ...props
}: BondSearchInputContainerProps) {
  return (
    <div className={cn('relative w-full', className)} {...props}>
      {children}
    </div>
  )
}

interface BondSearchInputResultMessageProps
  extends React.LiHTMLAttributes<HTMLLIElement> {
  children: React.ReactNode
  variant?: 'loading' | 'error' | 'empty'
}

export function BondSearchInputResultMessage({
  children,
  variant = 'empty',
  className,
  ...props
}: BondSearchInputResultMessageProps) {
  const base = 'px-4 py-3 text-sm'
  const color =
    variant === 'loading' || variant === 'empty'
      ? 'text-gray-400'
      : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
        variant === 'error'
        ? 'text-red-500'
        : ''
  return (
    <li className={cn(base, color, className)} {...props}>
      {children}
    </li>
  )
}
